import { supabase } from './supabaseClient';
import type { User } from './types';
import type { User as SupabaseUser } from '@supabase/supabase-js';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import Logger from './utils/Logger';

/** Extract message from unknown error */
function getErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'object' && error !== null && 'message' in error) {
        return String((error as { message: unknown }).message);
    }
    return String(error);
}

let nativeAnalyticsModulePromise: Promise<typeof import('@capacitor-community/firebase-analytics') | null> | null = null;
const authStateListeners = new Set<(user: User | null) => void>();

type UserProfileOverrides = {
    displayName?: string | null;
    photoURL?: string | null;
    isPro?: boolean;
};

async function getNativeAnalytics() {
    if (!Capacitor.isNativePlatform()) {
        return null;
    }

    nativeAnalyticsModulePromise ??= import('@capacitor-community/firebase-analytics').catch((error) => {
        Logger.warn('Failed to load native analytics plugin', error);
        return null;
    });

    const analyticsModule = await nativeAnalyticsModulePromise;
    return analyticsModule?.FirebaseAnalytics ?? null;
}

// Map Supabase User to our App User type
const mapUser = (user: SupabaseUser, additionalData?: UserProfileOverrides): User | null => {
    if (!user) return null;
    return {
        uid: user.id,
        displayName: additionalData?.displayName
            ?? user.user_metadata?.full_name
            ?? user.email?.split('@')[0]
            ?? 'Artist',
        photoURL: additionalData?.photoURL ?? user.user_metadata?.avatar_url ?? null,
        email: user.email || null,
        isPro: additionalData?.isPro || false
    };
};

function notifyManualAuthListeners(user: User | null) {
    authStateListeners.forEach((listener) => {
        try {
            listener(user);
        } catch (error) {
            Logger.error('[Auth] Listener notification failed', error);
        }
    });
}

export const auth = {
    currentUser: null as User | null, // Will be updated by onAuthStateChanged
    onAuthStateChanged: (callback: (user: User | null) => void) => {
        authStateListeners.add(callback);

        // Initial session check
        supabase.auth.getSession().then(({ data: { session } }) => {
            if (session?.user) {
                fetchUserProfile(session.user, callback);
            } else {
                auth.currentUser = null;
                callback(null);
            }
        });

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
            if (session?.user) {
                fetchUserProfile(session.user, callback);
            } else {
                auth.currentUser = null;
                callback(null);
            }
        });

        return () => {
            authStateListeners.delete(callback);
            subscription.unsubscribe();
        };
    },
    signInWithGoogle: async (): Promise<void> => {
        try {
            Logger.debug('[Auth] Starting Google sign-in...');

            if (Capacitor.isNativePlatform()) {
                const redirectUrl = 'com.ardrawing.app://google-auth';

                Logger.debug('[Auth] Native platform detected. Using Supabase OAuth via Browser.');
                Logger.debug('[Auth] Redirect URL:', redirectUrl);

                const { data, error } = await supabase.auth.signInWithOAuth({
                    provider: 'google',
                    options: {
                        redirectTo: redirectUrl,
                        skipBrowserRedirect: true
                    }
                });

                if (error) {
                    Logger.error('[Auth] Native OAuth initiation error', error);
                    throw error;
                }

                if (!data?.url) {
                    throw new Error('No Google OAuth URL returned from Supabase.');
                }

                Logger.debug('[Auth] Opening native OAuth browser...');
                await Browser.open({ url: data.url });
            } else {
                const redirectUrl = window.location.origin;

                Logger.debug('[Auth] Redirect URL:', redirectUrl);

                const { error } = await supabase.auth.signInWithOAuth({
                    provider: 'google',
                    options: {
                        redirectTo: redirectUrl,
                        skipBrowserRedirect: false
                    }
                });

                if (error) {
                    Logger.error('[Auth] OAuth initiation error', error);
                    throw error;
                }

                Logger.debug('[Auth] OAuth initiated, opening browser...');
            }
        } catch (error: unknown) {
            Logger.error("[Auth] Google Sign In Error", error);
            throw new Error(getErrorMessage(error) || "Failed to sign in with Google", { cause: error });
        }
    },
    signInWithEmail: async (email: string, password: string): Promise<User> => {
        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            });

            if (error) throw error;
            if (!data.user) throw new Error("No user returned");

            return mapUser(data.user)!;
        } catch (error: unknown) {
            Logger.error("Email Sign In Error", error);
            throw new Error(getErrorMessage(error) || "Failed to sign in", { cause: error });
        }
    },
    signUpWithEmail: async (email: string, password: string, displayName: string): Promise<User> => {
        try {
            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: displayName
                    }
                }
            });

            if (error) throw error;
            if (!data.user) throw new Error("No user returned");

            // Create user row in public.users table (if not handled by trigger)
            const { error: dbError } = await supabase
                .from('users')
                .insert({
                    id: data.user.id,
                    email: email,
                    display_name: displayName,
                    created_at: new Date().toISOString()
                });

            if (dbError) {
                Logger.warn("DB Insert Warning", dbError);
                // Don't fail auth just because DB insert failed (might be duplicate)
            }

            return mapUser(data.user)!;
        } catch (error: unknown) {
            Logger.error("Email Sign Up Error", error);
            throw new Error(getErrorMessage(error) || "Failed to create account", { cause: error });
        }
    },
    resetPassword: async (email: string): Promise<void> => {
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });
            if (error) throw error;
        } catch (error: unknown) {
            Logger.error("Password Reset Error", error);
            throw new Error(getErrorMessage(error) || "Failed to send reset email", { cause: error });
        }
    },
    signInWithFacebook: async (): Promise<void> => {
        // Facebook via Supabase OAuth
        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'facebook',
                options: {
                    // Use deep link for Android, fallback to origin for web
                    redirectTo: Capacitor.isNativePlatform()
                        ? 'com.ardrawing.app://facebook-auth'
                        : window.location.origin
                }
            });
            if (error) throw error;
        } catch (error: unknown) {
            Logger.error("Facebook Sign In Error", error);
            throw error;
        }
    },
    signInAnonymously: async (): Promise<User> => {
        try {
            const { data, error } = await supabase.auth.signInAnonymously();
            if (error) throw error;
            if (!data.user) throw new Error("No user returned");
            return mapUser(data.user)!;
        } catch (error) {
            Logger.error("Anonymous Sign In Error", error);
            throw error;
        }
    },
    getRedirectResult: async (): Promise<User | null> => {
        // Supabase handles redirect result automatically via session
        // This is kept for compatibility
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
            return mapUser(session.user);
        }
        return null;
    },
    signOut: async (): Promise<void> => {
        await supabase.auth.signOut();
        auth.currentUser = null;
    },
    refreshCurrentUser: async (): Promise<User | null> => {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session?.user) {
            auth.currentUser = null;
            notifyManualAuthListeners(null);
            return null;
        }

        const mappedUser = await resolveUserProfile(session.user);
        auth.currentUser = mappedUser;
        notifyManualAuthListeners(mappedUser);
        return mappedUser;
    },
    updateCurrentUser: (updates: Partial<User>): User | null => {
        if (!auth.currentUser) {
            return null;
        }

        const nextUser: User = {
            ...auth.currentUser,
            ...updates,
        };

        auth.currentUser = nextUser;
        notifyManualAuthListeners(nextUser);
        return nextUser;
    },
    upgradeToPro: async (uid: string) => {
        const { error } = await supabase.rpc('upgrade_user_to_pro', { user_id_param: uid });

        if (error) {
            Logger.error("Upgrade Error", error);
            throw error;
        }
    }
};
// Initialize Analytics (Wrapper for Native Plugin)
export const analytics = {
    initialize: async () => {
        try {
            const nativeAnalytics = await getNativeAnalytics();
            if (!nativeAnalytics) return;

            await nativeAnalytics.setCollectionEnabled({ enabled: true });
            Logger.debug('Analytics collection enabled');
        } catch (e) {
            console.error('Failed to initialize analytics:', e);
        }
    },
    logEvent: async (name: string, params?: Record<string, unknown>) => {
        try {
            const nativeAnalytics = await getNativeAnalytics();
            if (!nativeAnalytics) return;

            await nativeAnalytics.logEvent({ name, params: params ?? {} });
        } catch (e) {
            console.warn('Analytics logEvent failed:', e);
        }
    },
    setScreenName: async (screenName: string) => {
        try {
            const nativeAnalytics = await getNativeAnalytics();
            if (!nativeAnalytics) return;

            await nativeAnalytics.setScreenName({ screenName, nameOverride: screenName });
        } catch (e) {
            console.warn('Analytics setScreenName failed:', e);
        }
    },
    setUserId: async (userId: string) => {
        try {
            const nativeAnalytics = await getNativeAnalytics();
            if (!nativeAnalytics) return;

            await nativeAnalytics.setUserId({ userId });
        } catch (e) {
            console.warn('Analytics setUserId failed:', e);
        }
    }
};

// Helper: Fetch Full Profile (with timeout protection)
async function resolveUserProfile(authUser: SupabaseUser): Promise<User | null> {
    const { data, error } = await supabase
        .from('users')
        .select('display_name, photo_url, is_pro')
        .eq('id', authUser.id)
        .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 is "not found"
        Logger.error("Error fetching user profile", error);
    }

    return mapUser(authUser, {
        displayName: data?.display_name,
        photoURL: data?.photo_url,
        isPro: data?.is_pro || false,
    });
}

async function fetchUserProfile(authUser: SupabaseUser, callback: (user: User | null) => void) {
    let hasResolved = false;

    // Set a timeout to prevent hanging
    const timeoutId = setTimeout(() => {
        if (hasResolved) return;
        hasResolved = true;
        Logger.warn('Profile fetch timed out, using basic user info');
        const mappedUser = mapUser(authUser);
        auth.currentUser = mappedUser;
        callback(mappedUser);
    }, 5000); // 5 second timeout

    try {
        const mappedUser = await resolveUserProfile(authUser);

        clearTimeout(timeoutId);
        if (hasResolved) return; // Timeout already fired, don't call again
        hasResolved = true;
        auth.currentUser = mappedUser;
        callback(mappedUser);

    } catch (e) {
        clearTimeout(timeoutId);
        if (hasResolved) return;
        hasResolved = true;
        Logger.error("Profile Fetch Exception", e);
        const mappedUser = mapUser(authUser);
        auth.currentUser = mappedUser;
        callback(mappedUser);
    }
}

// Database & Storage Exports (Compatibility)
// We export these so other components using 'db' or 'storage' don't crash
// In a full migration, you'd replace these usages with direct 'supabase' calls
export const db = {
    // expose raw supabase client as 'db' if needed, or keeping it empty if we handle everything here
    // For now, we are deprecating direct 'db' usage in favor of using supabase directly in components
    // but to prevent crashes we might need dummy objects or a proxy
};

export const storage = {
    // Same for storage
};
